import { useEffect, useRef } from "react";
import { Check, CheckCheck, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import type {
  ContainerMessage,
  ContainerMessageSide,
} from "../../types/containerMessages";

/**
 * One container's conversation, rendered.
 *
 * Shared by the cab and the office deliberately, and not only to save a file:
 * the two ends are reading the same thread, and a driver told by their own
 * screen that a message was seen at 11:58 must be looking at the same sentence
 * the dispatcher is. Two components would be two chances for the sides to
 * disagree about which bubble is whose.
 *
 * `side` is who *this* app is — it decides which bubbles are outgoing, not
 * anything about the data.
 */

/** A message's words, in the reader's language when that is possible. */
const messageText = (
  message: ContainerMessage,
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  if (!message.templateKey) return message.body;

  /**
   * A quick message is stored as a key *and* as its English text, and this is
   * why: the key is translated for whoever is reading, so an Estonian driver's
   * tap reaches an Estonian dispatcher in Estonian. The stored body is the
   * fallback for a button added after this client shipped — better the message
   * in English than a raw key on a screen.
   */
  const key = `messages.templates.${message.templateKey}`;
  const translated = t(key);
  return translated === key ? message.body : translated;
};

const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/** The day a message belongs to, for the separators. */
const dayKeyOf = (iso: string) => new Date(iso).toDateString();

interface Props {
  messages: ContainerMessage[];
  /** Which end of the channel this app is. */
  side: ContainerMessageSide;
  loading?: boolean;
  hasMore?: boolean;
  onLoadOlder?: () => void;
  /** Shown when the thread has never been used. */
  emptyText?: string;
  className?: string;
}

export function ContainerMessageList({
  messages,
  side,
  loading = false,
  hasMore = false,
  onLoadOlder,
  emptyText,
  className = "",
}: Props) {
  const { t } = useTranslation("containerOrders");
  const listRef = useRef<HTMLDivElement | null>(null);
  /** Whether the reader is at the newest message, and so should be kept there. */
  const atEndRef = useRef(true);
  const lastId = messages[messages.length - 1]?._id;

  /**
   * Follow the end of the conversation.
   *
   * Keyed on the last message's id rather than on the array, so scrolling up
   * through the history — which replaces the array — does not yank the view
   * back down to the bottom the moment older messages load.
   *
   * The list's own `scrollTop`, never `scrollIntoView`: that scrolls every
   * ancestor too — the page and the document — which on a phone pushed the
   * header off the top on each send and left it there.
   */
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
    atEndRef.current = true;
  }, [lastId]);

  /**
   * Stay on the newest message when the list is resized under it — the
   * keyboard opening shortens it by half the screen, which would otherwise
   * bury the last few messages behind the composer.
   */
  const hasList = messages.length > 0;
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const observer = new ResizeObserver(() => {
      if (atEndRef.current) list.scrollTop = list.scrollHeight;
    });
    observer.observe(list);
    return () => observer.disconnect();
  }, [hasList]);

  if (!messages.length) {
    return (
      <div
        className={`flex min-h-0 flex-1 items-center justify-center px-6 py-10 text-center text-[13px] text-[#8A9590] ${className}`}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          (emptyText ?? t("messages.empty"))
        )}
      </div>
    );
  }

  let lastDay: string | null = null;

  return (
    <div
      ref={listRef}
      onScroll={(e) => {
        const list = e.currentTarget;
        atEndRef.current =
          list.scrollHeight - list.scrollTop - list.clientHeight < 40;
      }}
      // `overscroll-contain`: flicking past either end must not carry on to the
      // page, which on iOS with the keyboard up slides the whole view.
      className={`min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain px-3 py-3 ${className}`}
    >
      {hasMore && (
        <button
          type="button"
          onClick={onLoadOlder}
          disabled={loading}
          className="mx-auto flex items-center gap-1.5 rounded-full border border-[#E4E2DA] bg-white px-3 py-1 text-[12px] text-[#6B6A64] disabled:opacity-50"
        >
          {loading && <Loader2 className="h-3 w-3 animate-spin" />}
          {t("messages.loadOlder")}
        </button>
      )}

      {messages.map((message) => {
        const mine = message.from === side;
        const day = dayKeyOf(message.createdAt);
        const newDay = day !== lastDay;
        lastDay = day;

        return (
          <div key={message._id}>
            {/* A conversation about a container routinely spans a shift
                change and a night. Without the date, "07:15" on the message
                that explains a four-hour delay could be any morning. */}
            {newDay && (
              <p className="my-3 text-center text-[11px] text-[#A6A49C]">
                {new Date(message.createdAt).toLocaleDateString()}
              </p>
            )}

            <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className="max-w-[85%]">
                {/* Only on incoming messages: the sender of an outgoing one is
                    the person reading it. On the office's screen this is the
                    driver's name, which is the answer to "who told us" on a
                    container two drivers shared. */}
                {!mine && message.authorName && (
                  <p className="mb-0.5 px-1 text-[11px] text-[#A6A49C]">
                    {message.authorName}
                  </p>
                )}

                <div
                  className={`rounded-2xl px-3 py-2 text-[13.5px] leading-snug ${
                    mine
                      ? "rounded-br-sm bg-[#B4711F] text-white"
                      : "rounded-bl-sm border border-[#DCE7F2] bg-white text-[#1A1A18]"
                  }`}
                >
                  {messageText(message, t)}
                </div>

                <div
                  className={`mt-0.5 flex items-center gap-1 px-1 text-[10.5px] text-[#A6A49C] ${
                    mine ? "justify-end" : "justify-start"
                  }`}
                >
                  <span>{timeOf(message.createdAt)}</span>
                  {/* The receipt, on outgoing messages only — a tick on a
                      message you received tells you nothing you did not know
                      by reading it. One tick means delivered and stored, two
                      that the other end has opened the thread. */}
                  {mine &&
                    (message.readAt ? (
                      <CheckCheck
                        className="h-3.5 w-3.5 text-[#B4711F]"
                        aria-label={t("messages.seen")}
                      />
                    ) : (
                      <Check
                        className="h-3.5 w-3.5"
                        aria-label={t("messages.sent")}
                      />
                    ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}

    </div>
  );
}
